import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useContractorData } from "../hooks";
import { useContractorContext } from "../context";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { Module } from "@/constants";
import { PasswordResetByAdminConfirmDialog } from "@/components/PasswordResetByAdminConfirmDialog";
import { useResetContractorPassword } from "@/hooks/useResetContractorPassword";

interface ContractorMenuProps {
  setAgreementDialogOpen: (open: boolean) => void;
}

export const ContractorMenu = ({
  setAgreementDialogOpen,
}: ContractorMenuProps) => {
  const { t } = useTranslation();
  const contractor = useContractorData();
  const { can_update: canUpdateContractor, contractors_password_reset } =
    useModulePermissions(Module.Contractors);
  const { can_create: can_createAgreement } = useModulePermissions(
    Module.Agreements,
  );
  const { handleActivate, handleDeactivate, loadingState } =
    useContractorContext();
  const resetContractorPassword = useResetContractorPassword(contractor.id);

  const isAnyActionPending =
    loadingState.isLoading || resetContractorPassword.isPending;
  const canActivateContractor = canUpdateContractor && !contractor.is_active;
  const canDeactivateContractor = canUpdateContractor && contractor.is_active;

  if (
    !canUpdateContractor &&
    !contractors_password_reset &&
    !can_createAgreement
  ) {
    return null;
  }

  return (
    <Menubar className="mb-2">
      <MenubarMenu>
        <MenubarTrigger>{t("contractors.menubar.trigger")}</MenubarTrigger>
        <MenubarContent>
          {canDeactivateContractor && (
            <MenubarItem
              onClick={handleDeactivate}
              disabled={isAnyActionPending}
            >
              {isAnyActionPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("contractors.menubar.deactivate")}
            </MenubarItem>
          )}

          {contractors_password_reset && (
            <PasswordResetByAdminConfirmDialog
              onConfirm={resetContractorPassword.onConfirm}
            >
              <MenubarItem
                onSelect={(e) => e.preventDefault()}
                disabled={isAnyActionPending}
              >
                {t("contractors.menubar.resetPassword")}
              </MenubarItem>
            </PasswordResetByAdminConfirmDialog>
          )}

          {canActivateContractor && (
            <MenubarItem onClick={handleActivate} disabled={isAnyActionPending}>
              {isAnyActionPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("contractors.menubar.activate")}
            </MenubarItem>
          )}

          {can_createAgreement && (
            <MenubarItem onClick={() => setAgreementDialogOpen(true)}>
              {t("contractors.menubar.addAgreement")}
            </MenubarItem>
          )}
        </MenubarContent>
      </MenubarMenu>
    </Menubar>
  );
};
